/// <amd-module name="VSS/Msal/MsalAuthentication" />
import { AccountInfo, AuthenticationResult, AuthError, BrowserAuthError, CacheLookupPolicy, ClientAuthError, ClientAuthErrorCodes, Configuration, EventMessage, EventType, InteractionRequiredAuthError, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication, RedirectRequest, ServerError } from "@azure/msal-browser";
import { IObservable, Observable } from "VSS/Core/Observable";
import { getBearerTokenAuthHeaderValue } from "VSS/Platform/Authentication";
import { IContributedTokenProviderService } from "VSS/Platform/Authentication.types";
import { IAuthorizationTokenProvider, IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { publishEvent } from "VSS/Platform/Telemetry";
import { getCookie } from "VSS/Platform/Util/Cookie";
import { tryGetLocalStorageValue, tryGetSessionStorageObject, trySetLocalStorageValue, trySetSessionStorageObject } from "VSS/Platform/Util/Storage";

const FeatureArea = "MsalTokenProviderService";
const InitPerfScenarioName = "MsalTokenProvider.initialize";
const PopupScenarioName = "MsalTokenProvider.acquireTokenPopup";
const RedirectTelemetryStorageKey = "webPlatform.msalTokenProvider.acquireTokenRedirect";
const CacheWatermarkStorageKey = "webPlatform.msalTokenProvider.cacheWatermark";

interface RedirectTelemetry {
    startEpochMilliseconds: number;
    hasLocation?: boolean;
    previousSessionId?: string;
}

/**
 * Configuration information required to support running MSAL.js in the browser
 */
export interface MsalConfiguration {
    accessMapping: string;
    configuration: Configuration;
    fatalErrorCodes?: string[];
    scopes: string[];
    cacheLookupPolicy?: CacheLookupPolicy;
    cacheWatermark?: number;
}

export interface IPublicClientApplicationFactory extends IVssService {
    getPublicClientApplication(configuration: Configuration): IPublicClientApplication;
}

class PublicClientApplicationFactory extends VssService implements IPublicClientApplicationFactory {
    public getPublicClientApplication(configuration: Configuration): IPublicClientApplication {
        return new PublicClientApplication(configuration);
    }
}

Services.add("IPublicClientApplicationFactory", { serviceFactory: PublicClientApplicationFactory });

export class MsalTokenProviderService extends VssService implements IContributedTokenProviderService {
    private _disabled: boolean = false;
    private _initialized: Observable<boolean> = new Observable<boolean>();

    private _pca: IPublicClientApplication;
    private _provider: MsalTokenProvider;

    private _msalCallbackId: string | null = null;
    private _telemetryDisabled: boolean = false;

    private _popupTokenProvider: PopupMsalTokenProvider;
    private _silentTokenProvider: SilentMsalTokenProvider;

    _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // Retrieve the sessionId from the page so we can tag our telemetry data with it.
        // This service persists across FPS (unlike the Telemetry service)
        // So by getting this value in _serviceStart, we get the "first" sessionId and stick with it. This helps us correlate events across redirects and FPS
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();
        if (pageData) {
            _msalTokenProviderSessionId = pageData.sessionId;
        }

        // get and clear the stored redirect telemetry
        const storedTelemetryValue = tryGetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey);
        trySetSessionStorageObject(RedirectTelemetryStorageKey, undefined);

        if (storedTelemetryValue?.previousSessionId) {
            _msalTokenProviderSessionId = storedTelemetryValue.previousSessionId;
        }

        const perfService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario = _startPerfScenario(perfService, InitPerfScenarioName, storedTelemetryValue?.startEpochMilliseconds, {
            hasLocation: storedTelemetryValue?.hasLocation || false,
        });

        // we will not attempt token acquisition if we're in an iframe
        if (window.self !== window.top) {
            console.log("iframe detected; MSAL token provider is disabled");
            this._disabled = true;
        }

        // to disable the whole MSAL.js stack:
        //      document.cookie = "tfs-aad-token-disabled=true; path=/;"
        // to reenable:
        //      document.cookie = "tfs-aad-token-disabled=false; path=/"
        // ... and refresh the page
        if (getCookie("tfs-aad-token-disabled") == "true") {
            console.log("MSAL token provider disabled via tfs-aad-token-disabled cookie");
            this._disabled = true;
        }

        const provider = this._getProvider();
        if (!provider) {
            // MSAL is not enabled. we are done
            console.log("MSAL.js token provider disabled");
            _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.disabled");
            setTimeout(() => this._completeInitialization(), 0);
        }
        else {
            if (storedTelemetryValue?.startEpochMilliseconds) {
                // also note the navigation start time from this instance of the page, so we can see how long we were away from ADO
                perfScenario.splitTimings.push({
                    name: "backFromInteractive",
                    timestamp: perfService.getNavigationStartTime() - perfScenario.startTime
                });
            }

            _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.initializePromise.start");

            // MSAL.js 3.0 requires .initialize() to be called on the PCA before doing anything else
            this._pca.initialize().then(() => {
                _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.initializePromise.end");
                // attempt to acquire a token very early in the page lifecycle.
                // this gives us an opportunity to do a full-page redirect if the user is not logged in, before we start rendering content

                // first, we have to handle any redirect promise
                // this will complete any acquireTokenRedirect or loginRedirect flows. if none were in flight, the result will be null
                console.log("MSAL.js token provider initialization start");
                _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.handleRedirectPromise.start");
                this._pca.handleRedirectPromise().then((result: AuthenticationResult | null) => {
                    _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.handleRedirectPromise.end");

                    if (result) {
                        this._pca.setActiveAccount(result.account);

                        console.log("MSAL.js token provider initialization completed via handleRedirectPromise");
                        this._completeInitialization();
                    }
                    else {
                        console.log("MSAL.js token provider init: no in-flight redirect promise");
                        this._initialize(provider);
                    }
                }).catch((error) => {
                    console.log("MSAL.js token provider init: handling in-flight redirect promise failed: " + error);
                    this._initialize(provider);
                });
            }).catch((error) => {
                _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.initializePromise.end");
                console.log("PublicClientApplication.initialize failed: " + error);
                this._disabled = true;
                this._completeInitialization();
            });
        }
    }

    private _initialize(provider: MsalTokenProvider) {
        const perfService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        let account: AccountInfo | null = null;
        try {
            account = this._pca.getActiveAccount() || this._pca.getAllAccounts()[0];
        }
        catch (error) {
            console.log(error);

            // something is wrong with the token cache. let's log out and try to clean it up
            this._logout(provider);

            // we are logging out. that will navigate away from the page
            // just in case, we will not fire the authenticationInitialized event, which will prevent anything else from trying to acquire the token
            return;
        }

        if (account) {
            // we'll attempt to acquire a token silently. if that succeeds, then everything is great
            // if not, we may need to redirect the user to log in, e.g. when the refresh token is expired
            console.log("MSAL.js token provider init: acquireTokenSilent");
            _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.acquireTokenSilent.start");
            provider.acquireTokenSilent(account || undefined).then((acquireTokenSilentResult) => {
                _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.acquireTokenSilent.end");

                if (acquireTokenSilentResult.interactionRequired) {
                    console.log("MSAL.js token provider init: interaction required");

                    // we are going to redirect as part of initialization, so we want to continue the scenario on the next iteration
                    trySetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey, {
                        startEpochMilliseconds: perfService.getActiveScenario(InitPerfScenarioName)?.startTime || perfService.getNavigationStartTime()
                    });

                    // if the redirect succeeds, this promise will never resolve, which is fine
                    provider.acquireTokenRedirect().catch((reason) => {
                        console.log("MSAL.js token provider init: acquireTokenRedirect failed with " + reason);

                        // log out and try again
                        this._logout(provider);
                    });
                }
                else {
                    // if there is an error, let's log it and hope the next acquisition attempt succeeds
                    if (acquireTokenSilentResult.error) {
                        console.log(acquireTokenSilentResult.error);
                    }

                    console.log("MSAL.js token provider initialization complete");
                    this._completeInitialization();
                }
            });
        }
        else {
            // we are going to redirect as part of initialization, so we want to continue the scenario on the next iteration
            trySetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey, {
                startEpochMilliseconds: perfService.getActiveScenario(InitPerfScenarioName)?.startTime || perfService.getNavigationStartTime(),
                previousSessionId: _msalTokenProviderSessionId
            });

            // we don't have an account. redirect to log in
            console.log("MSAL.js token provider init: loginRedirect");
            provider.loginRedirect();
        }
    }

    private _logout(provider: MsalTokenProvider) {
        console.log("MSAL.js token provider init: calling logoutRedirect");
        this._pca.logoutRedirect(({
            onRedirectNavigate: () => {
                return false;
            }
        })).then(() => {
            provider.loginRedirect();
        });
    }

    public get initialized(): IObservable<boolean> {
        return this._initialized;
    }

    public async clearCache(): Promise<void> {
        if (!!this._pca) {
            return await this._pca.clearCache();
        }
    }

    public async logout(): Promise<void> {
        if (this._disabled) {
            return;
        }

        if (!!this._pca) {
            try {
                // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/logout.md
                await this._pca.logoutRedirect({
                    onRedirectNavigate: (url) => {
                        // this will prevent navigation to the identity provider after "local logout"
                        // because we want the normal logout flow to continue and take us to SPS
                        return false;
                    }
                });
            }
            catch (error) {
                console.log(error);
            }
        }
    }

    public getDefaultProvider(): IAuthorizationTokenProvider | undefined {
        // we will default to a popup provider (for now?) because we don't want to break flows where a user clicks a button, like "Run pipeline" or "Save work item"
        // a redirect in either of these would break the user experience (they'd have to re-click the button after reauthenticating, or re-do their work item edits)
        // flows where we know a redirect is okay (like FPS or other navigation-type events) should use the Redirect provider explicitly
        return this.getPopupProvider();
    }

    public getSilentProvider(): IAuthorizationTokenProvider | undefined {
        if (!this._silentTokenProvider) {
            const baseProvider = this._getProvider();
            if (!baseProvider) {
                return undefined;
            }

            this._silentTokenProvider = new SilentMsalTokenProvider(baseProvider,
                () => this._telemetryDisabled = false,
                () => this._telemetryDisabled = true);
        }

        return this._silentTokenProvider;
    }

    public getPopupProvider(): IAuthorizationTokenProvider | undefined {
        if (!this._popupTokenProvider) {
            const baseProvider = this._getProvider();
            if (!baseProvider) {
                return undefined;
            }

            this._popupTokenProvider = new PopupMsalTokenProvider(baseProvider);
        }

        return this._popupTokenProvider;
    }

    public getRedirectProvider(targetLocation?: string): IAuthorizationTokenProvider | undefined {
        const baseProvider = this._getProvider();
        if (!baseProvider) {
            return undefined;
        }

        return new RedirectMsalTokenProvider(baseProvider, targetLocation);
    }

    private _getProvider(): MsalTokenProvider | undefined {
        if (this._disabled) {
            return undefined;
        }

        if (!this._provider) {
            // if the msal-config-data is not present, then the feature is off
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const msalConfiguration = contributionService.getData<MsalConfiguration>("ms.vss-web.msal-config-data");

            const configuration = msalConfiguration?.configuration;
            if (!configuration) {
                console.log("no MSAL configuration found. MSAL token provider is disabled");
                this._disabled = true;
                return undefined;
            }

            // clear the cache if not done before this date
            const watermark = parseInt(tryGetLocalStorageValue(CacheWatermarkStorageKey, "0") || "0");
            if (msalConfiguration.cacheWatermark && (isNaN(watermark) || msalConfiguration.cacheWatermark > watermark)) {
                this._publishEvent({
                    eventType: "clearCache",
                    description: "clearing localStorage because the watermark is out of date"
                });
                localStorage.clear();
                trySetLocalStorageValue(CacheWatermarkStorageKey, Math.max(new Date().getTime(), msalConfiguration.cacheWatermark).toString());
            }

            if (!configuration.cache) {
                configuration.cache = {
                    cacheLocation: "localStorage", // enables SSO between browser tabs for the same app
                    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
                };
            }

            if (!configuration.system) {
                configuration.system = {};
            }

            if (!configuration.system.loggerOptions) {
                configuration.system.loggerOptions = {};
            }

            const logLevelOverride = parseInt(getCookie("tfs-msaljs-loglevel"));
            if (!isNaN(logLevelOverride)) {
                configuration.system.loggerOptions.logLevel = logLevelOverride;
            }

            configuration.system.loggerOptions.loggerCallback = (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }

                this._recordMsalLogs(level, message);

                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    case LogLevel.Trace:
                        console.trace(message);
                        return;
                }
            };

            const pcaProviderService = this.pageContext.getService<IPublicClientApplicationFactory>("IPublicClientApplicationFactory");
            this._pca = pcaProviderService.getPublicClientApplication(configuration);

            this._provider = new MsalTokenProvider(
                this.pageContext,
                this._pca,
                msalConfiguration.scopes,
                msalConfiguration.accessMapping,
                msalConfiguration.fatalErrorCodes || [],
                msalConfiguration.cacheLookupPolicy || CacheLookupPolicy.Default);

            this._msalCallbackId = this._pca.addEventCallback(this._eventCallback);
        }

        return this._provider;
    }

    public override _serviceEnd(pageContext: IVssPageContext) {
        if (!!this._msalCallbackId) {
            this._pca.removeEventCallback(this._msalCallbackId);
        }
    }

    private _eventCallback = (message: EventMessage) => {
        // if we just failed to acquire a token, and then try to report events, we'll get into a loop
        // because reporting events will attempt to request a new token
        // so, don't report any more events until we successfully get a new token
        if (message.eventType == EventType.ACQUIRE_TOKEN_FAILURE && message.interactionType == InteractionType.Silent) {
            this._telemetryDisabled = true;
        }
        else if (message.eventType == EventType.ACQUIRE_TOKEN_SUCCESS) {
            this._telemetryDisabled = false;
        }

        // record perf telemetry
        if (message.eventType == EventType.POPUP_OPENED) {
            const perfService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            perfService.addSplitTiming("popupOpened", PopupScenarioName);
        }
        else if (message.interactionType == InteractionType.Popup &&
            (message.eventType == EventType.ACQUIRE_TOKEN_SUCCESS || message.eventType == EventType.ACQUIRE_TOKEN_FAILURE || message.eventType == EventType.LOGIN_FAILURE || message.eventType == EventType.LOGIN_SUCCESS)) {
            const perfService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            perfService.addSplitTiming("popupClosed", PopupScenarioName);
        }

        if (this._shouldReportEvent(message)) {
            // publish telemetry
            this._publishEvent({
                eventType: message.eventType,
                interactionType: message.interactionType,
                error: message.error?.message,
                msalCorrelationId: (message.payload as any)?.correlationId
            });
        }
    }

    private _shouldReportEvent(message: EventMessage): boolean {
        // we are leaving out acquireTokenStart because it's very noisy on the happy path
        // similarly we're filtering out acquireTokenSuccess unless it's involved with an interaction
        return message.eventType == EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS
            || message.eventType == EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE
            || message.eventType == EventType.ACQUIRE_TOKEN_FAILURE
            || message.eventType == EventType.ACQUIRE_TOKEN_NETWORK_START
            || message.eventType == EventType.LOGIN_FAILURE
            || message.eventType == EventType.LOGIN_START
            || message.eventType == EventType.LOGIN_SUCCESS
            || message.eventType == EventType.POPUP_OPENED
            || (message.eventType == EventType.ACQUIRE_TOKEN_SUCCESS
                && (message.interactionType == InteractionType.Popup || message.interactionType == InteractionType.Redirect));
    }

    private _recordMsalLogs(level: LogLevel, message: string) {
        // info and noisier messages will only go to the console
        // otherwise, telemetry will start generating its own Info-level telemetry as it acquires tokens, and we'll get in a loop
        if (level >= LogLevel.Info) {
            return;
        }

        this._publishEvent({
            eventType: "logging",
            level: level,
            message: message
        });
    }

    private _completeInitialization() {
        const perfService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        _recordInitializationSplitTimings(perfService, "MsalTokenProviderService.initializationComplete");
        perfService.endScenario(FeatureArea, InitPerfScenarioName, false, undefined, {
            isDisabled: this._disabled
        });

        document.dispatchEvent(new CustomEvent("authenticationInitialized", {}));
        this._initialized.notify(true, "initialized", true);
    }

    private _publishEvent(payload: { [key: string]: any }) {
        if (this._telemetryDisabled) {
            return;
        }

        publishEvent(this.pageContext, "WebPlatform", "MsalTokenProviderService", _withCommonProperties(payload));
    }
}

Services.add("IMsalTokenProviderService", { serviceFactory: MsalTokenProviderService, options: ServiceOptions.Startup | ServiceOptions.Persistant });

class SilentMsalTokenProvider implements IAuthorizationTokenProvider {
    private _provider: MsalTokenProvider;
    private _reportedSilentError: boolean = false;
    private _onSuccess: () => void;
    private _onError: () => void;

    constructor(provider: MsalTokenProvider, onSuccess: () => void, onError: () => void) {
        this._provider = provider;
        this._onSuccess = onSuccess;
        this._onError = onError;
    }

    get providerName(): string {
        return this._provider.providerName;
    }

    async getAuthorizationHeader(forceRefresh?: boolean | undefined): Promise<string> {
        if (forceRefresh) {
            await this._provider.clearCache();
        }

        const acquireTokenSilentResult = await this._provider.acquireTokenSilent(undefined, CacheLookupPolicy.AccessToken);
        if (acquireTokenSilentResult.token) {
            // we successfully acquired a token.  reset the error-reporting flag
            this._reportedSilentError = false;
            if (this._onSuccess) {
                this._onSuccess();
            }

            return getBearerTokenAuthHeaderValue(acquireTokenSilentResult.token);
        }
        else {
            if (acquireTokenSilentResult.error instanceof ClientAuthError) {
                const clientAuthError = acquireTokenSilentResult.error as ClientAuthError;
                if (clientAuthError.errorCode === ClientAuthErrorCodes.tokenRefreshRequired) {
                    // this is expected when there is no token in the cache. the silent provider will remain silent
                    return "";
                }
            }

            if (this._onError) {
                this._onError();
            }

            if (!this._reportedSilentError) {
                // only report the error once.  otherwise the reporting may generate a new error and we'll get into a loop
                this._reportedSilentError = true;

                this._provider.recordSilentFailure(acquireTokenSilentResult, "SilentMsalTokenProvider.error");
            }
        }

        // the silent provider will not throw out if it can't get a token. attempt the request anyway and let the service handle it
        return "";
    }
}

class RedirectMsalTokenProvider implements IAuthorizationTokenProvider {
    private _provider: MsalTokenProvider;
    private _targetLocation: string | undefined;

    constructor(provider: MsalTokenProvider, targetLocation?: string) {
        this._provider = provider;
        this._targetLocation = targetLocation;
    }

    get providerName(): string {
        return this._provider.providerName;
    }

    async getAuthorizationHeader(forceRefresh?: boolean | undefined): Promise<string> {
        if (forceRefresh) {
            try {
                await this._provider.clearCache();
                return await this._provider.acquireTokenRedirect(this._targetLocation);
            }
            catch (error) {
                console.log(error);
            }
        }
        else {
            const acquireTokenSilentResult = await this._provider.acquireTokenSilent();
            if (acquireTokenSilentResult.token) {
                return getBearerTokenAuthHeaderValue(acquireTokenSilentResult.token);
            }
            else {
                if (acquireTokenSilentResult.error) {
                    this._provider.recordSilentFailure(acquireTokenSilentResult, "RedirectMsalTokenProvider.error");
                }

                if (acquireTokenSilentResult.interactionRequired) {
                    try {
                        return await this._provider.acquireTokenRedirect(this._targetLocation);
                    }
                    catch (error) {
                        console.log(error);
                    }
                }
            }
        }

        return "";
    }
}

class PopupMsalTokenProvider implements IAuthorizationTokenProvider {
    private _provider: MsalTokenProvider;

    constructor(provider: MsalTokenProvider) {
        this._provider = provider;
    }

    get providerName(): string {
        return this._provider.providerName;
    }

    async getAuthorizationHeader(forceRefresh?: boolean | undefined): Promise<string> {
        let token = "";

        if (forceRefresh) {
            await this._provider.clearCache();
            token = await this._provider.acquireTokenPopup();
        }
        else {
            const acquireTokenSilentResult = await this._provider.acquireTokenSilent();
            if (acquireTokenSilentResult.token) {
                token = acquireTokenSilentResult.token;
            }
            else {
                if (acquireTokenSilentResult.error) {
                    this._provider.recordSilentFailure(acquireTokenSilentResult, "PopupMsalTokenProvider.error");
                }

                if (acquireTokenSilentResult.interactionRequired) {
                    token = await this._provider.acquireTokenPopup();
                }
            }
        }

        if (token) {
            return getBearerTokenAuthHeaderValue(token);
        }
        else {
            return "";
        }
    }
}

interface AcquireTokenSilentResult {
    token?: string;
    interactionRequired?: boolean;
    error?: any;
}

interface AcquireTokenResult {
    token?: string;
    error?: any;
}

class MsalTokenProvider {
    private _activePromise?: Promise<AcquireTokenResult>;
    private _accessMapping: string;
    private _fatalErrorCodes: string[];
    private _pageContext: IVssPageContext;
    private _pca: IPublicClientApplication;
    private _scopes: string[];
    private _cacheLookupPolicy: CacheLookupPolicy;
    private _pageData: IPageData | undefined;

    public constructor(pageContext: IVssPageContext, pca: IPublicClientApplication, scopes: string[], accessMapping: string, fatalErrorCodes: string[], cacheLookupPolicy: CacheLookupPolicy) {
        this._accessMapping = accessMapping;
        this._fatalErrorCodes = fatalErrorCodes;
        this._pageContext = pageContext;
        this._pca = pca;
        this._scopes = scopes;
        this._cacheLookupPolicy = cacheLookupPolicy;

        this._pageData = this._pageContext.getService<IVssPageService>("IVssPageService").getData();
    }

    get providerName(): string {
        return "MsalTokenProvider";
    }

    public async acquireTokenSilent(account?: AccountInfo, cacheLookupPolicy: CacheLookupPolicy = this._cacheLookupPolicy): Promise<AcquireTokenSilentResult> {
        const acquireTokenResult = await this._acquireToken(async () => {
            try {
                account = account || this._pca.getActiveAccount() || this._pca.getAllAccounts()[0];
                const authenticationResult = await this._acquireTokenSilent(account, cacheLookupPolicy);
                return {
                    token: this._handleAuthenticationResult(authenticationResult)
                };
            }
            catch (error) {
                if (this._isFatalError(error)) {
                    await this._handleFatalError(error);
                }

                return {
                    error: error
                };
            }
        });

        return {
            error: acquireTokenResult.error,
            interactionRequired: this._isInteractionRequiredError(acquireTokenResult.error),
            token: acquireTokenResult.token
        }
    }

    public async acquireTokenPopup(): Promise<string> {
        const acquireTokenResult = await this._acquireToken(async () => {
            try {
                const authenticationResult = await this._acquireTokenPopup();
                return {
                    token: authenticationResult.accessToken
                }
            }
            catch (error) {
                return {
                    error: error
                }
            }
        });

        return acquireTokenResult.token ?? "";
    }

    public async acquireTokenRedirect(targetLocation?: string): Promise<string> {
        const acquireTokenResult = await this._acquireToken(async () => {
            if (!tryGetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey)) {
                trySetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey, {
                    startEpochMilliseconds: new Date().getTime(),
                    hasLocation: !!targetLocation,
                    previousSessionId: _msalTokenProviderSessionId
                });
            }

            await this._acquireTokenRedirect(targetLocation);

            // _acquireTokenRedirect will actually redirect the page so unless something goes wrong we'll never get here.
            // this return is just to satisfy the compiler
            // if _acquireTokenRedirect throws, the caller can handle the error
            return {
                token: ""
            }
        });

        return acquireTokenResult.token ?? "";
    }

    public async loginRedirect(targetLocation?: string): Promise<void> {
        await this._acquireToken(async () => {
            if (!tryGetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey)) {
                trySetSessionStorageObject<RedirectTelemetry>(RedirectTelemetryStorageKey, {
                    startEpochMilliseconds: new Date().getTime(),
                    previousSessionId: _msalTokenProviderSessionId
                });
            }

            console.log("calling loginRedirect");
            await this._pca.loginRedirect(this._getRedirectRequest(targetLocation));

            // loginRedirect will actually redirect the page so we'll never get here. this is just to satisfy the compiler
            return {
                token: ""
            }
        });
    }

    public async clearCache(): Promise<void> {
        return await this._pca.clearCache();
    }

    public publishEvent(payload: { [key: string]: any }) {
        publishEvent(this._pageContext, "WebPlatform", "MsalTokenProviderService", _withCommonProperties(payload));
    }

    public recordSilentFailure(acquireTokenSilentResult: AcquireTokenSilentResult, eventType: string) {
        const eventPayload: any = {
            eventType: eventType,
            error: acquireTokenSilentResult.error?.message,
            interactionRequired: acquireTokenSilentResult.interactionRequired
        };

        if (acquireTokenSilentResult.error instanceof AuthError) {
            eventPayload.msalCorrelationId = acquireTokenSilentResult.error.correlationId;
            eventPayload.errorCode = acquireTokenSilentResult.error.errorCode;
            eventPayload.subError = acquireTokenSilentResult.error.subError;
        }

        this.publishEvent(eventPayload);
    }

    private _acquireToken(tokenAcquisition: () => Promise<AcquireTokenResult>): Promise<AcquireTokenResult> {
        if (!this._activePromise) {
            this._activePromise = tokenAcquisition();
        }

        this._activePromise.finally(() => {
            delete this._activePromise;
        });

        return this._activePromise;
    }

    private async _acquireTokenSilent(account: AccountInfo, cacheLookupPolicy: CacheLookupPolicy): Promise<AuthenticationResult> {
        const scenarioName = "MsalTokenProvider.acquireTokenSilent";

        // we only record telemetry when the silent acquisition did not find the result in the cache
        // we'll capture the start time for now, and only create the scenario if necessary
        const start = Date.now();

        const authenticationResult = await this._pca.acquireTokenSilent({
            account,
            scopes: this._scopes,
            cacheLookupPolicy: cacheLookupPolicy,
            state: this._getMsalState("popup"), // type "popup" redirects to a completely blank page with no script, which is what we want in the case of a silent iframe acquisition
        });

        if (!authenticationResult.fromCache) {
            const perfService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            _startPerfScenario(perfService, scenarioName, start);
            perfService.endScenario(FeatureArea, scenarioName, false);
        }

        return authenticationResult;
    }

    private async _acquireTokenPopup(): Promise<AuthenticationResult> {
        const perfService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        _startPerfScenario(perfService, PopupScenarioName, undefined);

        try {
            return await this._pca.acquireTokenPopup({
                scopes: this._scopes,
                state: this._getMsalState("popup"),
            });
        }
        finally {
            perfService.endScenario(FeatureArea, PopupScenarioName, false);
        }
    }

    private _acquireTokenRedirect(targetLocation?: string): Promise<void> {
        const redirectRequest = this._getRedirectRequest(targetLocation);

        console.log("calling acquireTokenRedirect");
        return this._pca.acquireTokenRedirect(redirectRequest);
    }

    private _getRedirectRequest(targetLocation?: string): RedirectRequest {
        return {
            scopes: this._scopes,
            state: this._getMsalState("redirect"),
            redirectStartPage: targetLocation
        };
    }

    private _handleAuthenticationResult(authenticationResult: AuthenticationResult): string {
        this._pca.setActiveAccount(authenticationResult.account);
        return authenticationResult.accessToken;
    }

    private _isFatalError(error: any): boolean {
        return this._fatalErrorCodes.some(errorCode => error.message?.includes(errorCode));
    }

    private async _handleFatalError(error: any): Promise<void> {
        console.log(error);

        await this._pca.logoutRedirect(({
            onRedirectNavigate: () => {
                return false;
            }
        }));

        await this._pca.loginRedirect(this._getRedirectRequest());
    }

    private _isInteractionRequiredError(error: any) {
        if (!error) {
            return false;
        }
        else if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
            return true;
        }
        else if (error instanceof ServerError) {
            const serverError = error as ServerError;
            return serverError.errorCode === "invalid_grant";
        }

        return false;
    }

    private _getMsalState(type: "popup" | "redirect"): string {
        return JSON.stringify({
            host: this._pageData?.hostName,
            service: "tfs",
            isLegacyDomain: this._accessMapping === "VstsAccessMapping",
            type: type,
        })
    }
}

let _msalTokenProviderSessionId: string = "";

function _startPerfScenario(perfService: IVssPerformanceService, scenarioName: string, startTime: number | undefined, properties?: { [key: string]: any }): IVssScenario {
    return perfService.startScenario(scenarioName, false, startTime, _withCommonProperties(properties));
}

// we want to add split timings to the page-load scenario
// but we also track a separate scenario to handle the redirect flows
function _recordInitializationSplitTimings(perfService: IVssPerformanceService, timingName: string) {
    perfService.addSplitTiming(timingName, InitPerfScenarioName);
    perfService.addSplitTiming(timingName);
}

function _withCommonProperties(payload?: { [key: string]: any }): { [key: string]: any } {
    if (!payload) {
        payload = {};
    }

    payload.msalTokenProviderSessionId = _msalTokenProviderSessionId;

    return payload;
}
